import React from 'react';

import {
  Bot,
  Import,
  Map,
  Megaphone,
  MessageSquareText,
  SquareChevronUp,
  UserRoundCheck,
} from 'lucide-react';

import {
  IntegrationCards,
  type IntegrationLinkCard,
} from 'common/subdomain/admin/AdminIntegrationSettings/constants';
import devURL from 'common/util/devURL';

import {
  CollectFeedbackOptionValue,
  CompanySizeValue,
  IntegrationOptionValue,
  MotivationOptionValue,
  SourcesOptionValue,
  SyncIntegrationOptionValue,
  TeamOptionValue,
  ToolOptionValue,
} from './constants';

import AsanaLogo from 'img/asana-logo.png';
import AzureDevOpsLogo from 'img/azure-devops-logo-small.png';
import ClickupLogo from 'img/clickup-logo.png';
import DiscordLogo from 'img/discord-logo-small.png';
import GithubLogo from 'img/github-logo-icon.png';
import GongLogo from 'img/gong-logo-small.png';
import HelpscoutLogo from 'img/helpscout-logo-small.png';
import HubspotLogo from 'img/hubspot-logo-small.png';
import IdentifySDKLogo from 'img/identify-sdk-logo.svg';
import IntercomLogo from 'img/intercom-logo-small.png';
import JiraLogo from 'img/jira-logo.svg';
import LinearLogo from 'img/linear-logo-small.png';
import MicrosoftTeamsLogo from 'img/microsoft-teams-logo-small.png';
import SalesforceLogo from 'img/salesforce-logo-small.png';
import SlackLogo from 'img/slack-logo-small.png';
import ZendeskLogo from 'img/zendesk-logo-small.png';
import ZoomLogo from 'img/zoom-logo-small.png';

import type { AdminOnboardingOption } from './AdminOnboardingMultiSelect';
import type { Option } from 'common/ui/common/select/SelectCommon';

export const whatOptions: AdminOnboardingOption[] = [
  {
    name: MotivationOptionValue.Feedback,
    icon: MessageSquareText,
    label: 'Collect feedback',
    description: 'Capture and organize feedback from all sources',
  },
  {
    name: MotivationOptionValue.Roadmap,
    icon: Map,
    label: 'Roadmap',
    description: 'Organize feedback to assess impact and prioritize',
  },
  {
    name: MotivationOptionValue.Announcements,
    icon: Megaphone,
    label: 'Announcements',
    description: 'Share product updates with your customers',
  },
];

export const howOptions: AdminOnboardingOption[] = [
  {
    name: CollectFeedbackOptionValue.Autopilot,
    icon: Bot,
    label: 'Autopilot AI',
    description: 'Automatically capture feedback from customer conversations and calls',
  },
  {
    name: CollectFeedbackOptionValue.Direct,
    icon: SquareChevronUp,
    label: 'Feedback Portal',
    description: <>Users submit feedback by posting and&nbsp;voting</>,
  },
  {
    name: CollectFeedbackOptionValue.Import,
    icon: Import,
    label: 'Import feedback',
    description: <>Upload CSV files to pull in existing&nbsp;feedback</>,
  },
  {
    name: CollectFeedbackOptionValue.Behalf,
    icon: UserRoundCheck,
    label: 'Track manually',
    description: <>Submit feedback on behalf of users via&nbsp;integrations</>,
  },
];

export const communicationToolOptions: AdminOnboardingOption[] = [
  { name: ToolOptionValue.Intercom, label: 'Intercom', img: IntercomLogo },
  { name: ToolOptionValue.Zendesk, label: 'Zendesk', img: ZendeskLogo },
  { name: ToolOptionValue.Slack, label: 'Slack', img: SlackLogo },
  { name: ToolOptionValue.HelpScout, label: 'Help Scout', img: HelpscoutLogo },
  { name: ToolOptionValue.Zoom, label: 'Zoom', img: ZoomLogo },
  { name: ToolOptionValue.Gong, label: 'Gong', img: GongLogo },
  { name: ToolOptionValue.Hubspot, label: 'HubSpot', img: HubspotLogo },
  { name: ToolOptionValue.Salesforce, label: 'Salesforce', img: SalesforceLogo },
];

export const internalToolOptions: AdminOnboardingOption[] = [
  { name: ToolOptionValue.Slack, label: 'Slack', img: SlackLogo },
  { name: ToolOptionValue.MicrosoftTeams, label: 'Microsoft Teams', img: MicrosoftTeamsLogo },
  { name: ToolOptionValue.Discord, label: 'Discord', img: DiscordLogo },
];

export const projectManagementToolOptions: AdminOnboardingOption[] = [
  { name: ToolOptionValue.Jira, label: 'Jira', img: JiraLogo },
  { name: ToolOptionValue.Clickup, label: 'Clickup', img: ClickupLogo },
  { name: ToolOptionValue.Linear, label: 'Linear', img: LinearLogo },
  { name: ToolOptionValue.Asana, label: 'Asana', img: AsanaLogo },
  { name: ToolOptionValue.Github, label: 'Github', img: GithubLogo },
  { name: ToolOptionValue.AzureDevOps, label: 'Azure DevOps', img: AzureDevOpsLogo },
];

export const teamOptions: AdminOnboardingOption[] = [
  {
    name: TeamOptionValue.Leadership,
    label: 'Leadership',
  },
  {
    name: TeamOptionValue.ProductManagers,
    label: 'Product managers',
  },
  {
    name: TeamOptionValue.ProductOwners,
    label: 'Product owners',
  },
  {
    name: TeamOptionValue.Sales,
    label: 'Sales',
  },
  {
    name: TeamOptionValue.CustomerSuccess,
    label: 'Customer success',
  },
  {
    name: TeamOptionValue.CustomerSupport,
    label: 'Customer support',
  },
  {
    name: TeamOptionValue.Engineering,
    label: 'Engineering',
  },
  {
    name: TeamOptionValue.Design,
    label: 'Design',
  },
  {
    name: TeamOptionValue.Operations,
    label: 'Operations',
  },
  {
    name: TeamOptionValue.Marketing,
    label: 'Marketing',
  },
  {
    name: TeamOptionValue.Other,
    label: 'Other',
  },
];

export const companySizeOptions: Option[] = [
  {
    label: '1',
    value: CompanySizeValue.One,
  },
  {
    label: '2-10',
    value: CompanySizeValue.TwoToTen,
  },
  {
    label: '11-50',
    value: CompanySizeValue.ElevenToFifty,
  },
  {
    label: '51-500',
    value: CompanySizeValue.FiftyOneToFiveHundred,
  },
  {
    label: '501-1,000',
    value: CompanySizeValue.FiveHundredOneToOneThousand,
  },
  {
    label: '1,001',
    value: CompanySizeValue.OneThousandOneToFiveThousand,
  },
  {
    label: '5,001+',
    value: CompanySizeValue.FiveThousandOnePlus,
  },
];

export const roleOptions: Option[] = [
  {
    label: 'Executive / Leadership',
    value: 'executive_leadership',
  },
  {
    label: 'Product Manager',
    value: 'product_manager',
  },
  {
    label: 'Product Owner',
    value: 'product_owner',
  },
  {
    label: 'Engineer / Developer',
    value: 'engineer_developer',
  },
  {
    label: 'Designer / UX',
    value: 'designer_ux',
  },
  {
    label: 'Sales',
    value: 'sales',
  },
  {
    label: 'Customer Success',
    value: 'customer_success',
  },
  {
    label: 'Customer Support',
    value: 'customer_support',
  },
  {
    label: 'Marketing',
    value: 'marketing',
  },
  {
    label: 'Operations',
    value: 'operations',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

type OnboardingIntegrationCard = IntegrationLinkCard & {
  description: string;
  installButtonTitle?: string;
};

export type SourcesOptionValueKey = (typeof SourcesOptionValue)[keyof typeof SourcesOptionValue];

export const feedbackSources: Record<SourcesOptionValueKey, OnboardingIntegrationCard> = {
  [SourcesOptionValue.Intercom]: {
    ...IntegrationCards.intercom,
    description: 'Capture feedback in customer support conversations',
  },
  [SourcesOptionValue.HelpScout]: {
    ...IntegrationCards.helpscout,
    description: 'Capture feedback in customer support conversations',
  },
  [SourcesOptionValue.Zendesk]: {
    ...IntegrationCards.zendesk,
    description: 'Capture feedback in customer support tickets',
  },
  [SourcesOptionValue.Zoom]: {
    ...IntegrationCards.zoom,
    description: 'Capture feedback in sales call transcripts',
  },
  [SourcesOptionValue.Gong]: {
    ...IntegrationCards.gong,
    description: 'Capture feedback in sales call transcripts',
  },
};

export type IntegrationOptionValueKey =
  (typeof IntegrationOptionValue)[keyof typeof IntegrationOptionValue];

export const integrations: Record<IntegrationOptionValueKey, OnboardingIntegrationCard> = {
  [IntegrationOptionValue.Hubspot]: {
    ...IntegrationCards.hubspot,
    description: 'Track feedback, link deals, and sync customer data',
  },
  [IntegrationOptionValue.Salesforce]: {
    ...IntegrationCards.salesforce,
    description: 'Track feedback, link opportunities, and sync customer data',
  },
  [IntegrationOptionValue.Slack]: {
    ...IntegrationCards.slack,
    description: 'Track feedback in Slack and get notifications for activities in Canny',
  },
  [IntegrationOptionValue.MicrosoftTeams]: {
    ...IntegrationCards.microsoftTeams,
    description: 'Get notifications for activities in Canny',
  },
  [IntegrationOptionValue.Discord]: {
    ...IntegrationCards.discord,
    description: 'Get notifications for activities in Canny',
  },
  [IntegrationOptionValue.Jira]: {
    ...IntegrationCards.jira,
    description: 'Sync feedback between Canny and Jira tasks',
  },
  [IntegrationOptionValue.Clickup]: {
    ...IntegrationCards.clickup,
    description: 'Sync feedback between Canny and ClickUp tasks',
  },
  [IntegrationOptionValue.Linear]: {
    ...IntegrationCards.linear,
    description: 'Sync feedback between Canny and Linear issues',
  },
  [IntegrationOptionValue.Asana]: {
    ...IntegrationCards.asana,
    description: 'Sync feedback between Canny and Asana tasks',
  },
  [IntegrationOptionValue.Github]: {
    ...IntegrationCards.github,
    description: 'Sync feedback between Canny and GitHub issues',
  },
  [IntegrationOptionValue.AzureDevOps]: {
    ...IntegrationCards.azureDevops,
    description: 'Sync feedback between Canny and Azure DevOps work items',
  },
};

export type SyncIntegrationOptionValueKey =
  (typeof SyncIntegrationOptionValue)[keyof typeof SyncIntegrationOptionValue];

export const syncIntegrations: Record<SyncIntegrationOptionValueKey, OnboardingIntegrationCard> = {
  [SyncIntegrationOptionValue.Sdk]: {
    label: 'Identify SDK',
    icon: IdentifySDKLogo,
    url: devURL('https://developers.canny.io/install'),
    description: 'Typically takes an engineer less than 15 minutes to set up',
    installButtonTitle: 'Set up',
  },
  [SyncIntegrationOptionValue.Segment]: {
    ...IntegrationCards.segment,
    description: 'Sync user and company data from Segment',
  },
  [SyncIntegrationOptionValue.Salesforce]: {
    ...IntegrationCards.salesforce,
    description: 'Sync account, contact, and opportunity data from Salesforce',
  },
  [SyncIntegrationOptionValue.Hubspot]: {
    ...IntegrationCards.hubspot,
    description: 'Sync company, contact, and deal data from HubSpot',
  },
};
